import { useCallback, useEffect, useRef } from 'react';

import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { SurveyContent, type Answers } from '@/modules/survey';
import { LoadingScreen } from '@/modules/ui';

import { useDynamicAssessmentSession } from './hooks/useDynamicAssessmentSession';
import { toDynamicSurveyQuestions } from './utils/toDynamicSurveyQuestions';

/** Why the dynamic flow handed control back to the parent. */
export type DynamicAssessmentDoneReason = 'exhausted' | 'error';

/** Terminal outcomes the dynamic pass surfaced, handed to the parent on done. */
export type DynamicAssessmentResult = {
  /**
   * Set when phq_gad_v1's terminal carried a crisis-line result (Sparta's
   * requiresCrisisLine + qualifiedProgramUrns). The parent defers it into the
   * rules engine so survey completion routes to the crisis program screen.
   */
  crisisProgramUrns?: string[];
  /**
   * The dynamic flow keys that completed in this pass — the backend's `/flows/`
   * statuses unioned with the flows the pass itself drove to a terminal (see
   * `useDynamicAssessmentSession`). The parent latches it so forward nav skips a
   * standalone screener (pre-diabetes / phq-gad) only when its flow actually
   * completed — guarding against silently dropping a screener whose flow was
   * unpublished/abandoned.
   */
  completedFlowKeys?: readonly string[];
};

type DynamicAssessmentFlowProps = {
  /**
   * Called once the dynamic flows hand back control. The parent decides what's
   * next — in the test arm it runs the legacy screener flow. The `reason` lets
   * it tell a clean finish ('exhausted') from a failure ('error'): on 'error'
   * the answers may not have been written to the legacy tables (no terminal
   * effect fired), so the parent falls back to the full legacy survey walk
   * instead of excluding the dynamic surveys.
   */
  onDone: (reason: DynamicAssessmentDoneReason, result?: DynamicAssessmentResult) => void;
};

/**
 * Drives the onboarding dynamic-assessment flows sequentially (health conditions
 * → goals → AOM screeners → diabetes decision → pre-diabetes → phq/gad) — the
 * test-arm `/step/dynamic-assessment` survey step. Mounted by
 * DynamicAssessmentScreen only when `onb_use_dynamic_assessment` is on, so it never
 * needs to read the flag itself.
 *
 * The hook handles gate auto-transition and instant-skip terminals transparently;
 * the screen only renders when there is a question node to show. On exhaustion
 * (all flows done or skipped) or on error, calls `onDone` with the reason — the
 * parent then runs the legacy flow for the screeners dynamic assessment doesn't
 * support yet (and, on error, latches the fallback so the whole survey region
 * re-runs legacy). A phq_gad_v1 crisis terminal rides along in the result.
 */
export function DynamicAssessmentFlow({ onDone }: DynamicAssessmentFlowProps) {
  const { user } = useRulesEngine();
  const {
    content,
    isLoading,
    isSubmitting,
    isExhausted,
    completedFlowKeys,
    crisisProgramUrns,
    error,
    submit,
  } = useDynamicAssessmentSession();

  const startedSessionRef = useRef<string | null>(null);
  // Guards the once-per-mount funnel events (arm entry + terminal handoff) AND the
  // onDone handoff against a double-fire — StrictMode double-invoke, or
  // `shouldAdvance` re-flipping when `crisisProgramUrns` resolves in the same
  // render as a terminal. Explicit so the once-per-mount contract holds.
  const armEnteredRef = useRef(false);
  const doneFiredRef = useRef(false);

  const onContinue = useCallback(
    async (answers: Answers) => {
      if (!content) return;
      trackEvent(`dynamic-assessment-continue-${content.flowKey}`, {
        uuid: user?.uuid,
        flowKey: content.flowKey,
        sessionUuid: content.sessionUuid,
      });
      // `submit` swallows its own errors and surfaces them via `error`, which the
      // shouldAdvance effect below turns into onDone('error') — so there's no throw
      // to catch here (a submit failure hands off to the parent, like the legacy flow).
      await submit(answers);
    },
    [content, user?.uuid, submit]
  );

  // All flows done ('exhausted') or a load/submit error ('error') → hand control
  // back to the parent, telling it which so it can decide whether to fall back to
  // legacy. A captured crisis outcome rides along on the clean finish.
  const shouldAdvance = !isLoading && (isExhausted || error != null);

  // Arm-entry event: fires once when the test arm mounts this flow, BEFORE any
  // load/submit-failed event can fire — so it's the clean denominator for the
  // completion and fallback rates (unlike screener-started, which a first-flow
  // load failure would skip).
  useEffect(() => {
    if (armEnteredRef.current || !user?.uuid) return;
    armEnteredRef.current = true;
    trackEvent('dynamic-assessment-started', { uuid: user.uuid });
  }, [user?.uuid]);

  // Terminal handoff. On a clean finish emit `dynamic-assessment-completed` (the
  // numerator for completion rate); the error path's counters live in the session
  // hook, and the actual legacy re-serve is tracked in AssignedScreenersScreen.
  // ref-guarded so the funnel events + onDone fire exactly once. A captured crisis
  // outcome rides along in the result.
  useEffect(() => {
    if (!shouldAdvance || doneFiredRef.current) return;
    doneFiredRef.current = true;
    const reason = error != null ? 'error' : 'exhausted';
    if (reason === 'exhausted') {
      trackEvent('dynamic-assessment-completed', { uuid: user?.uuid });
    }
    onDone(reason, { crisisProgramUrns, completedFlowKeys });
  }, [shouldAdvance, error, crisisProgramUrns, completedFlowKeys, onDone, user?.uuid]);

  // Fire "screener-started" once per flow as its question loads (deduped on the
  // session UUID), mirroring the legacy flow's per-screener event. Keyed by the
  // dynamic flowKey — intentionally NOT remapped to the legacy screener_name:
  // the two arms emit distinct identifiers by design (which also keeps them
  // distinguishable in analysis); reconcile them at the dashboard layer.
  useEffect(() => {
    if (content && user?.uuid && startedSessionRef.current !== content.sessionUuid) {
      startedSessionRef.current = content.sessionUuid;
      trackEvent(`screener-started-${content.flowKey}`, {
        uuid: user.uuid,
        screenerName: content.flowKey,
        sessionUuid: content.sessionUuid,
      });
    }
  }, [content, user?.uuid]);

  if (isLoading || shouldAdvance || !content) {
    return <LoadingScreen />;
  }

  const questions = toDynamicSurveyQuestions(
    content.uiPayload.elements,
    content.uiPayload.title,
    content.uiPayload.description
  );

  return (
    <SurveyContent
      key={content.sessionUuid}
      questions={questions}
      onContinue={onContinue}
      isSubmitting={isSubmitting}
      testIDPrefix={`dynamic-assessment-${content.flowKey}`}
    />
  );
}
